import { useMemo } from 'react'
import type { AppDispatch } from '@redux/store'

import { matchFilterKey, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import * as agencyActions from '@redux/modules/agency'

import type { EntityOptions } from '@redux/modules/types'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { AgencyModel, AgencyRequestOptions } from '@domain-group/realhub-backend-api-types'

const watchEntityKeys = ['agencies']

type LoadAgencyIdsParams = {
  agencyIds: number[],
  dispatch: AppDispatch,
  requestOptions?: EntityOptions,
}

const loadAgencyIds = (params: LoadAgencyIdsParams) => {
  const { agencyIds, dispatch, requestOptions } = params
  const { loadAgencyIds: loadFn } = agencyActions

  return dispatch(loadFn(agencyIds, requestOptions))
}

type AgencyFilters = {
  brand_id?: number,
  string?: string,
}

type UseAgenciesOptions = {
  filters?: AgencyFilters & { limit?: number },
  performHttpRequests?: boolean,
  requestOptions?: AgencyRequestOptions,
}

function useAgencies(options: UseAgenciesOptions) {
  const { filters = {}, requestOptions } = options
  const {
    brand_id: filterBrandId,
    limit: filterLimit,
    string: filterString,
  } = filters

  const {
    updatedEntities: { agencies: agenciesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const dispatch = useDispatch()

  const { agencies } = useSelector(reduxState => reduxState.entities)

  // Filter redux entities with provided filters
  const filteredAgencies = useMemo(() => {
    const filtered = Object.values(agencies).filter((agency: AgencyModel) => {
      const { brand_id, id, title } = agency

      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle

      // @ts-expect-error TS2345
      const matchBrand = matchFilterKey(brand_id, filterBrandId)

      return matchBrand && matchString
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [agenciesUpdatedAt, JSON.stringify(filters)])

  const filteredAgenciesCount = filteredAgencies.length
  const hasFilteredAgencies = !!filteredAgenciesCount

  // To be used in conjunction with DashboardModule.LoadMoreFooter
  const loadMorePayload = useLoadMore({
    filters,
    limit: filterLimit,
    loadedCount: filteredAgenciesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  // Load entities when performHttpRequests is true
  const reduxActionPayload = useReduxAction(
    'agencies',
    'loadAgencies',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }) => performHttpRequests && !loading,
    },
  )

  const { loading: loadingAgencies, reduxActionPerformed } = reduxActionPayload

  return {
    agencies,
    callbacks: {
      loadAgencyIds: (params: { agencyIds: number[], entityOptions?: EntityOptions }) => {
        const { agencyIds, entityOptions } = params
        return loadAgencyIds({ agencyIds, dispatch, requestOptions: entityOptions })
      },
      loadMore,
    },
    canLoadMore,
    filteredAgencies,
    filteredAgenciesCount,
    hasFilteredAgencies,
    loading: loadingAgencies,
    reduxActionPerformed,
  }
}

export default useAgencies
